import { ArrowRight, BarChart3, Lightbulb, ListFilter, PlaySquare, Repeat2 } from "lucide-react";
import { Link } from "react-router-dom";
import SEO, { breadcrumbSchema } from "../Components/SEO";
import TheNav from "../Components/navbar";

const MINT = "#84ff00";
const PURPLE = "#9146FF";
const HAIR = "rgba(255,255,255,0.09)";
const SURF = "rgba(255,255,255,0.035)";
const path = "/how-to-use-social-media-analytics";
const title = "How to Use Social Media Analytics to Grow Content | Kre8 Content";
const description = "Learn how creators, streamers, and social media managers can use video views, engagement, retention, and content context to make stronger next-post decisions.";

const steps = [
  {
    icon: BarChart3,
    title: "Start with comparable posts",
    copy: "Group posts by the thing you are actually testing: a topic, format, hook, video length, creative style, or campaign. Comparing unrelated posts creates noise instead of a useful lesson.",
  },
  {
    icon: ListFilter,
    title: "Read more than the view count",
    copy: "Views show reach. Likes, comments, shares, and available watch-time or retention signals add context about whether people responded to the post after seeing it.",
  },
  {
    icon: Lightbulb,
    title: "Turn a pattern into one next test",
    copy: "Write a small hypothesis such as: shorter clips about a specific game earn stronger early attention. Then change one meaningful variable in the next post rather than copying everything blindly.",
  },
  {
    icon: Repeat2,
    title: "Review the next batch",
    copy: "One post is not proof. Compare several attempts, keep the pattern only when it repeats, and use the next result to refine the content decision again.",
  },
];

const signals = [
  ["Views", "Use them to compare reach between similar posts. A high-view post is a lead to investigate, not automatic proof that every detail caused the result."],
  ["Likes, comments, and shares", "Use engagement to understand the response behind reach. Comments can reveal discussion; shares can suggest a post was useful or worth passing along."],
  ["Watch time and retention", "When a platform makes these signals available, use them to compare how long people stayed. Treat them as clues about pacing, hooks, and structure."],
  ["Length, topic, and format", "Pair performance with the creative choices you made. This is what turns a spreadsheet of numbers into a usable content hypothesis."],
  ["Twitch broadcast context", "For streamers, review broadcast titles and durations alongside short-form results to find streams, moments, or themes worth repurposing."],
];

const faq = [
  ["What is social media analytics?", "Social media analytics is the practice of reviewing performance data such as views, engagement, watch time, retention, and content context to understand what your audience responds to and decide what to test next."],
  ["Which social media metrics matter most?", "The useful metrics depend on the decision you are making. Views help compare reach; engagement helps compare response; watch time and retention can help compare attention; length, topic, and format explain the creative context behind those numbers."],
  ["Can social media analytics guarantee more views?", "No. Analytics does not guarantee a result. It helps you replace guesses with clearer hypotheses, then test those ideas consistently."],
];

function FaqSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map(([question, answer]) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
}

export default function HowToUseSocialMediaAnalytics() {
  return <div className="min-h-screen overflow-x-hidden bg-[#07060d] text-white">
    <SEO
      title={title}
      description={description}
      canonicalPath={path}
      structuredData={[
        breadcrumbSchema([
          { name: "Social Media Stats Tracker", path: "/stats" },
          { name: "How to Use Social Media Analytics", path },
        ]),
        FaqSchema(),
      ]}
    />
    <TheNav />
    <main>
      <section className="relative overflow-hidden px-5 pb-20 pt-16 sm:px-8 sm:pb-28 sm:pt-24">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[580px]" style={{ background: `radial-gradient(50% 62% at 50% 0%, ${PURPLE}2e, transparent 72%)` }} />
        <div className="relative mx-auto max-w-4xl text-center">
          <p className="inline-flex rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em]" style={{ borderColor: `${MINT}66`, color: MINT, background: `${MINT}12` }}>Social media analytics guide</p>
          <h1 className="mx-auto mt-6 max-w-4xl text-4xl font-semibold leading-[1.03] tracking-[-0.04em] sm:text-6xl">How to use social media analytics to make better content decisions.</h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/65 sm:text-lg">Do not chase one viral number. Compare similar posts, look for repeatable signals, and turn what you learn into one focused test for the next post.</p>
          <Link to="/signup?next=%2Fstats" className="mt-9 inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-6 text-sm font-semibold text-white transition hover:brightness-110" style={{ background: PURPLE, boxShadow: `0 12px 38px -14px ${PURPLE}` }}>Start your 7-day free trial <ArrowRight aria-hidden="true" className="h-4 w-4" /></Link>
          <p className="mt-4 text-xs text-white/40">Card required. Free for 7 days, then $10 USD/month unless canceled.</p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: MINT }}>A practical framework</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">Use data to form a better hypothesis, not to predict the next viral post.</h2>
        </div>
        <ol className="mt-10 grid gap-4 md:grid-cols-2">
          {steps.map(({ icon: Icon, title: heading, copy }, index) => <li key={heading} className="rounded-2xl border p-6" style={{ borderColor: HAIR, background: SURF }}>
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold" style={{ color: MINT, background: `${MINT}18` }}>{index + 1}</span>
            <Icon aria-hidden="true" className="mt-5 h-5 w-5" style={{ color: "#c9a8ff" }} />
            <h3 className="mt-4 text-lg font-semibold">{heading}</h3>
            <p className="mt-2 text-sm leading-relaxed text-white/60">{copy}</p>
          </li>)}
        </ol>
      </section>

      <section className="border-y px-5 py-16 sm:px-8 sm:py-24" style={{ borderColor: HAIR, background: "rgba(255,255,255,.025)" }}>
        <div className="mx-auto max-w-6xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: MINT }}>What to compare</p>
          <h2 className="mt-3 max-w-3xl text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">The number matters less than the question it helps you answer.</h2>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {signals.map(([heading, copy]) => <article key={heading} className="rounded-2xl border bg-[#07060d] p-6" style={{ borderColor: HAIR }}>
              <h3 className="text-lg font-semibold">{heading}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/60">{copy}</p>
            </article>)}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-4xl px-5 py-16 sm:px-8 sm:py-24">
        <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: MINT }}>A useful example</p>
        <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">Turn the data into a next-post brief.</h2>
        <div className="mt-8 rounded-2xl border p-6 sm:p-8" style={{ borderColor: HAIR, background: SURF }}>
          <p className="text-sm font-semibold text-white/50">Instead of:</p>
          <p className="mt-2 text-lg font-medium text-white/75">“That video did well, so I should make another one.”</p>
          <p className="mt-6 text-sm font-semibold" style={{ color: MINT }}>Try:</p>
          <p className="mt-2 text-lg font-medium text-white">“My shorter posts about streamer growth earned stronger reach and engagement than my other recent formats. I will test another concise, opinion-led post on that topic with a different opening hook.”</p>
        </div>
        <p className="mt-6 text-sm leading-relaxed text-white/60">That is a measurable experiment. It gives you a reason for the next post, a clear variable to watch, and a way to learn whether the pattern repeats.</p>
      </section>

      <section className="mx-auto w-full max-w-4xl px-5 pb-16 sm:px-8 sm:pb-24">
        <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: MINT }}>Questions</p>
        <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">Social media analytics FAQ</h2>
        <div className="mt-8 divide-y rounded-2xl border px-6" style={{ borderColor: HAIR, background: SURF }}>
          {faq.map(([question, answer]) => <details key={question} className="group py-5">
            <summary className="cursor-pointer list-none pr-8 text-base font-semibold text-white marker:hidden">{question}</summary>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-white/60">{answer}</p>
          </details>)}
        </div>
        <div className="mt-12 rounded-3xl border px-6 py-12 text-center" style={{ borderColor: HAIR, background: `radial-gradient(60% 100% at 50% 0%, ${PURPLE}33, transparent 70%), ${SURF}` }}>
          <PlaySquare aria-hidden="true" className="mx-auto h-6 w-6" style={{ color: MINT }} />
          <h2 className="mx-auto mt-4 max-w-xl text-2xl font-semibold tracking-[-0.03em] sm:text-3xl">Put your next content decision next to the data behind it.</h2>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-white/60">Compare TikTok, Instagram Reels, and Twitch signals in one tracker.</p>
          <Link to="/stats" className="mt-7 inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-6 text-sm font-semibold text-white transition hover:brightness-110" style={{ background: PURPLE }}>Open the stats tracker <ArrowRight aria-hidden="true" className="h-4 w-4" /></Link>
        </div>
      </section>
    </main>
  </div>;
}
